import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getUploadUrl,
  uploadFileToS3,
  markUploadComplete,
  deleteMedia,
  GetUploadUrlPayload,
} from '../api/media';
import { QUERY_KEYS } from 'service/querykeys';

/** Invalidates the parent item's children-infinite cache after a media change on a child. */
const bustParentChildrenCache = (
  queryClient: ReturnType<typeof useQueryClient>,
  projectUuid: string,
  workItemUuid: string,
) => {
  const queriesData = queryClient.getQueriesData<any>({
    queryKey: ['task', projectUuid, workItemUuid],
  });
  let parentUuid: string | undefined;
  for (const [, data] of queriesData) {
    if (data) {
      parentUuid = data.workItemParentWorkItemUuid || data.parentWorkItemUuid;
      if (parentUuid) break;
    }
  }
  if (parentUuid) {
    queryClient.invalidateQueries({
      queryKey: ['task-children-infinite', projectUuid, parentUuid],
      exact: false,
    });
    queryClient.invalidateQueries({
      queryKey: ['task', projectUuid, parentUuid],
      exact: false,
    });
  }
};

export const useMediaUpload = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      file,
      ...payload
    }: GetUploadUrlPayload & { file: File }) => {
      const response = await getUploadUrl(payload);
      if (!response.status || !response.url) {
        throw new Error(response.message || 'Failed to get upload URL');
      }
      await uploadFileToS3(response.url, file);
      await markUploadComplete(payload.projectUuid, response.mediaUuid);
      return response;
    },
    onSuccess: (_data, variables) => {
      if (variables.workItemUuid) {
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.TASK(variables.projectUuid, variables.workItemUuid),
        });
        bustParentChildrenCache(queryClient, variables.projectUuid, variables.workItemUuid);
      }
    },
  });
};

export const useDeleteMedia = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectUuid,
      mediaUuid,
    }: {
      projectUuid: string;
      mediaUuid: string;
      workItemUuid?: string;
    }) => deleteMedia(projectUuid, mediaUuid),
    onSuccess: (_data, variables) => {
      if (variables.workItemUuid) {
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.TASK(variables.projectUuid, variables.workItemUuid),
        });
        bustParentChildrenCache(queryClient, variables.projectUuid, variables.workItemUuid);
      }
    },
  });
};
